import type { ReactElement } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFlags } from '@flagsmith/flagsmith/react';

import { ApiRoute, Route } from 'config';
import { patch } from 'utils/network';
import { runsWebview } from 'utils/device';
import { postMessage, WebViewMessage } from 'utils/webview';
import useCurrentUser from 'hooks/useCurrentUser';

import Button from 'react-bootstrap/Button';
import RadioButton from 'components/shared/RadioButton';
import Dropdown from 'components/shared/Dropdown';

import { getOptionsforPause } from 'models/Subscription';

import { Component as CirclePauseIcon } from 'icons/icon-circle--pause.svg';
import CancelModalContainer from 'platform/components/cancellation/CancelModalContainer';
import SubscriptionStatus from '../SubscriptionStatus';

const pauseOptions = getOptionsforPause();

type Props = {
  handleErrors: () => void;
};

function PauseSubscription({ handleErrors }: Props): ReactElement {
  const { modal_cancellation_flow: modalCancellationFlow } = useFlags(['modal_cancellation_flow']);
  const isModalCancellationEnabled = modalCancellationFlow?.enabled && modalCancellationFlow?.value === 'show';
  const navigate = useNavigate();
  const {
    currentUser,
    reload,
  } = useCurrentUser();
  const {
    isDeactivated,
    isSuspended,
    isCancelled,
    subscriptionStatus,
  } = currentUser;
  const [pauseReason, setPauseReason] = useState(pauseOptions[0].label);
  const [showModalCancellation, setShowModalCancellation] = useState(false);

  const onPauseReasonChange = (value: string) => setPauseReason(value);

  const onMobilePauseReasonChange = (e) => {
    const selectedPauseOption = e.target.id;
    // @ts-expect-error TODO: fix null safety
    const pausePeriod = pauseOptions.find(
      (item) => item.id === selectedPauseOption,
    ).label;
    setPauseReason(pausePeriod);
  };

  const handleDeactivation = async () => {
    // @ts-expect-error TODO: fix null safety
    const weeksNumber = pauseOptions.find(
      (item) => item.label === pauseReason,
    ).id;

    const payload = {
      subscription: {
        status: 'returning',
        activate_after_weeks: weeksNumber,
      },
    };

    const response = await patch(`${ApiRoute.DEACTIVATE}`, payload);

    if (response.ok) {
      postMessage(WebViewMessage.SUBSCRIPTION_PAUSED);

      reload();

      // this is to support the tanstack setup in the new platform
      navigate(Route.UPCOMING_MENUS, {
        state: {
          invalidate: true,
        },
      });
    }
  };

  return (
    <>
      {!isSuspended && !isCancelled && (
        <SubscriptionStatus subscriptionStatus={subscriptionStatus} />
      )}

      <div
        id="subscription-management-pause"
        className={[
          'c-subscription-management-option',
          'is-active',
          isDeactivated ? 'is-disabled' : '',
        ].join(' ')}
        aria-controls="pause-subscription-content"
      >
        <div className="c-subscription-management-option__content">
          <div
            className={[
              'u-text',
              'u-text--center',
              'c-subscription-management-option__header',
              isDeactivated ? 'disabled' : '',
            ].join(' ')}
          >
            <div className="c-subscription-management-option__icon">
              <CirclePauseIcon
                height="36"
                width="36"
                className="c-svg-fill-gray-068-color"
              />
            </div>
            <h3 className="c-heading c-heading--brand-7">
              PAUSE YOUR SUBSCRIPTION
            </h3>
            <p className="u-text u-text--lg">
              Select this option to pause your subscription for up to 10 weeks.
              (Skip for up to 4 weeks in&nbsp;
              <a href={Route.UPCOMING_MENUS}>
                Upcoming Deliveries
              </a>
              .)
            </p>
          </div>

          <div
            id="pause-subscription-content"
            role="region"
            className={[
              'c-subscription-management-option__pause-form',
              'collapse',
              !isDeactivated ? 'show is-active' : '',
            ].join(' ')}
          >
            <form onSubmit={(e) => e.preventDefault()}>
              <fieldset className="c-form-group">
                {runsWebview ? (
                  <div
                    className="c-input-radio-group"
                    aria-label="Pause Subscription Reason"
                  >
                    {pauseOptions.map((option) => (
                      <RadioButton
                        key={option.id}
                        id={option.id}
                        label={option.label}
                        name="reason"
                        checked={option.label === pauseReason}
                        onChange={onMobilePauseReasonChange}
                      />
                    ))}
                  </div>
                ) : (
                  <Dropdown
                    selectedValue={pauseReason}
                    values={pauseOptions.map((option) => option.label)}
                    onSelect={onPauseReasonChange}
                  />
                )}
              </fieldset>
            </form>
            <Button
              className="d-block c-button c-button--primary"
              onClick={handleDeactivation}
              data-cy="manage-subscription-pause-subscription"
            >
              Pause Subscription
            </Button>
          </div>
        </div>
      </div>

      <div
        className={[
          'c-subscription-management-cancel-link__wrapper',
          runsWebview ? 'd-none' : '',
        ].join(' ')}
      >
        {isModalCancellationEnabled ? (
          <Button
            className="c-subscription-management-cancel-button"
            onClick={() => setShowModalCancellation(true)}
          >
            <span>
              Cancel Subscription
            </span>
          </Button>
        ) : (
          <Link
            to={`${Route.MANAGE_SUBSCRIPTION}?action=cancel`}
            className="c-subscription-management-cancel-link"
          >
            <span>
              Cancel Subscription
            </span>
          </Link>
        )}
      </div>

      {showModalCancellation && (
        <CancelModalContainer
          show={showModalCancellation}
          onClose={() => setShowModalCancellation(false)}
          handleErrors={handleErrors}
        />
      )}
    </>
  );
}

export default PauseSubscription;
